import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {AuthApi} from "components/src/api/portal/generated";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from "dompurify";
import {useEffect, useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from 'react-bootstrap/Form';

/*
 * REST api for accessing the back-end
 */
const authApi = new AuthApi(ApiConfig.config());

export const PortalForgotPassword = () =>
{
    /**
     * Email address
     */
    const [email, setEmail] = useState<string>();
    /**
     * true if a reset was sent
     */
    const [resetSent, setResetSent] = useState<boolean>(false);
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Ref for email input field
     */
    const emailInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const sanitizeHTML = (html : string) => {
        return {__html: DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })};
    }

    useEffect(() => {
        emailInputRef.current?.focus();
    }, []);

    const startPasswordReset = () =>
    {
        if (email === undefined) {
            throw new Error("email is undefined");
        }

        setErrorMessage(undefined);

        authApi.authControllerForgotPassword(email)
            .then(() =>
            {
                setResetSent(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error starting password reset", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
    }

    return (
        <>
        {t(['portal.forgot-password.header']).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.forgot-password.header"]))} />}
        <div className="w-100 p-4 shadow-sm rounded bg-body">
            <div className="mb-4 text-center">
                <h2>{t("portal.forgot-password.title")}</h2>
            </div>

            {/* show an alert if a validation error occurred */}
            <RenderAlert message={errorMessage}/>

            {resetSent && <RenderAlert message={t('portal.forgot-password.reset-sent')} variant="success"/>}

            <FloatingLabel label={t('portal.forgot-password.email-label')} className="text-secondary mb-3">
                <Form.Control type="email" value={email} placeholder="Email"
                              ref={emailInputRef}
                              onChange={e => {setEmail(e.target.value); setResetSent(false)}}/>
            </FloatingLabel>

            {t('portal.forgot-password.custom-text').length > 0 &&
                <div className="mt-2" dangerouslySetInnerHTML={sanitizeHTML(t(["portal.forgot-password.custom-text"]))} />
            }

            <Button variant="success" className="w-100 mt-4" disabled={StringUtils.isEmpty(email) || resetSent}
                    onClick={startPasswordReset}>
                {t('portal.forgot-password.reset-button')}
            </Button>
        </div>
        {t(['portal.forgot-password.footer']).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.forgot-password.footer"]))} />}
        </>
    );
}
